import React, { useCallback, useMemo,useState } from 'react';
import {
  Box,
  FlexListHeader,
  ListItem,
  OptionKey,
  PaginationBar,
  Preloader,
  SearchInput, 
  Text, usePaginationParams,
} from '@common-fe/common-fe';
import _ from 'lodash';

import routes from '@/common/routes';
import { ListItemBase, ListItemType } from '@/common/types';
import { WarnModal } from '@/components/elements';
import { ListPlaceholder } from '@/modules/CarrierConfig/components';
import { useContactDeactivateQuery } from '@/modules/core/hooks';
import { ContactResponseModel } from '@/modules/employer/components/SetupEmployer/ContactsSecurity/AddEditContactsSecurityModal/ContactSecurity.types';
import { DEFAULT_PAGE_SIZE,useContactListQuery } from '@/modules/employer/components/SetupEmployer/ContactsSecurity/AddEditContactsSecurityModal/queries/useContacts.queries';
import { RolesDropdown } from '@/modules/role';
import { useRolesAllowedToCreateQuery } from '@/modules/role/queries';

import ContactsSecurityTableListItem from './components/ContactsSecurityTableListItem';
import UnverifiedContactHint from './components/UnverifiedContactHint';

export interface Props {
  disabled?: boolean;
  isViewContactsAvailable?: boolean;
  disabledEdit?: boolean;
  organizationId?: string;
  triggerRefetching?: string;
  onEdit: (contact: ContactResponseModel) => void;
  onView: (contact: ContactResponseModel) => void;
}

const headers: ListItemBase[] = [{
  key: 'fullName',
  title: 'Full Name and Email',
  flex: 2,
}, {
  key: 'tilte',
  title: 'Title/Role',
}, {
  key: 'department',
  title: 'Department',
}, {
  key: 'permissions',
  title: 'Permissions',
},
];

const ContactsSecurityTable: React.FC<Props> = ({
  disabled,
  isViewContactsAvailable,
  disabledEdit,
  onEdit,
  onView,
  organizationId,
  triggerRefetching,
}) => {

  const [permissions, setPermissions] = useState<OptionKey[]>([]);
  
  const { options: rolesList = [] }=  useRolesAllowedToCreateQuery({
    disabled
  });
  const {
    page,
    setPage,
    setPerPage,
    perPage,
    searchString,
    setSearchString,
    currentSearch,
  } = usePaginationParams(DEFAULT_PAGE_SIZE);
  const { contactList, totalElements, isLoading } = useContactListQuery({
    ...(currentSearch ? { search: currentSearch } : {}),
    ...(!permissions.length ? {} : { permissionsIds: permissions.map((item) => _.toNumber(item)) }),
    organizationId,
    size: perPage,
    triggerRefetching,
    ...(page ? { page: page - 1 } : {}),
    // enabled: !disabled,
  });
  const [deactivatedItems, setDeactivatedItems] = useState<string[]>([]);
  const [deactivationItem, setDeactivationItem] = useState('');
  const contacts = useMemo(
    () => contactList.filter((item) => !deactivatedItems.some((id) => `${item.id}` === id)),
    [deactivatedItems, contactList],
  );
  const formattedList = useMemo<ListItem[]>(() => contacts.map((item) => ({
    id: `${item.id}` || '',
    fields: [
      {
        key: 'fullName',
        type: ListItemType.Contact,
        title: `${item.first_name} ${item.last_name}`,
        value: item.email,
        primary: item.primary,
        hover: !disabled,
        ...item?.is_email_verified ? {} : {
          valueRenderWrapper: (value) => <UnverifiedContactHint contactEmail={value} />,
        },
        flex: 2,
      },
      {
        key: 'title',
        type: ListItemType.Text,
        title: item.title || '',
      },
      {
        key: 'department',
        type: ListItemType.Text,
        title: item.department || '',
      },
      {
        key: 'permissions',
        type: ListItemType.Text,
        title: rolesList.find(
          (role)=> role.key === item.roleId
        )?.title || '-' ,
      },
      ...(disabled ? [{
        key: 'empty',
        type: ListItemType.Text,
        title: '',
        flex: 0.2,
      }] : []),
    ],
  }
  )), [contacts, disabled, rolesList]);
  const onSuccessDeactivation = () => {
    setDeactivatedItems([
      ...deactivatedItems,
      deactivationItem,
    ]);
  };
  const deactivateItem = useContactDeactivateQuery(onSuccessDeactivation);
  const deactivationItemTitle = useMemo(
    () => formattedList
      .find((item) => item.id === deactivationItem)?.fields
      .find((field) => field.key === 'fullName')?.title,
    [formattedList, deactivationItem],
  );

  const handleOpenLoginHistory = useCallback((contactId?: number) => {
    if (contactId) {
      const url = new URL(`${window.location.origin}${routes.CONTACT_LOGIN_HISTORY}`);
      const searchParams = new URLSearchParams(location.search);
      searchParams.forEach((value, key) => {
        url.searchParams.append(key, value);
      });
      url.searchParams.append('contactId', `${contactId}`);
      window.open(url.toString(), '_blank');
    }
  }, []);

  return (
    <Box background="canvas" round="container1Round" id="contact_information">
      <Box height="2px" width="100%" background="border2" />
      <Box margin="small" pad="small" background="module" round="container1Round">
        <Box direction="row" align="center" justify="between" margin={{ top: 'spacing8', bottom: 'spacing12' }}>
          <Text
            size="large"
            weight="bold"
            color="textBody"
          >{`Contacts: ${totalElements || 0}`}
          </Text>
          <Box direction="row">
            <Box width="240px">
              <SearchInput
                data-testid="ContactSecurityTable_search_input"
                hasSearchingHistory
                disabled={disabled}
                value={searchString}
                placeholder="Search"
                onChange={setSearchString}
              />
            </Box>
            <Box width="180px" margin={{ left: 'spacing12' }}>
              <RolesDropdown
                disabled={disabled}
                values={permissions}
                onChangeValues={setPermissions}
              />
      
            </Box>
          </Box>
        </Box>
        {formattedList?.length ? (
          <Box>
            <FlexListHeader items={headers} hasOption={!disabledEdit} />
            <Box style={{ position: 'relative' }}>
              {formattedList.map((row: ListItem, index) => (
                <ContactsSecurityTableListItem
                  key={contacts[index]?.id}
                  row={row}
                  contact={contacts[index]}
                  disableOptions={disabled || disabledEdit}
                  isViewContactsAvailable={isViewContactsAvailable}
                  onEdit={onEdit}
                  onView={onView}
                  onOpenLoginHistory={handleOpenLoginHistory}
                />
              ))}
            </Box>
            <PaginationBar
              page={page}
              total={totalElements || 0}
              pageSize={perPage}
              onChangePage={setPage}
              onChangePageSize={setPerPage}
            />
          </Box>
        ) : (
          isLoading ? <Box height="128px" justify="center"><Preloader color="iconOnColor" /></Box> : <ListPlaceholder>No results have been found</ListPlaceholder>
        )}
      </Box>

      <WarnModal
        visible={!!deactivationItem}
        onSetVisible={() => setDeactivationItem('')}
        header={`Are you sure you want to deactivate ${deactivationItemTitle}?`}
        buttonText="Cancel"
        submitButtonText="Yes, Deactivate"
        submitButtonMode="alert"
        onSubmit={() => deactivateItem(deactivationItem)}
      />
    </Box>
  );
};

export default ContactsSecurityTable;
